import React, { useEffect, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { TERRITORY_OPTIONS } from "@/hq/territory";
import { INDUSTRY_CATALOGUE, industryLabel } from "@/hq/industries";
import { PhoneCountryFields, isPhoneValidOrEmpty } from "@/optimise/PhoneCountryFields";

const STATUS_OPTIONS = ["prospect", "active", "inactive", "customer", "lead"];

/**
 * ClientFormModal — Phase 3G.12
 *
 * Reuses the shared PhoneCountryFields component (Phase 3C) so Country
 * and Phone country code are handled identically here, on the public
 * contact form (checkout modal) and in the Scan Review step. One source
 * of truth for the country list + dial codes.
 *
 * Storage contract (server accepts, primary contact + business row):
 *   phone         → E.164 (+442071234567)      → contact.phone, business.phone
 *   country_code  → ISO alpha-2 (GB / AE / US) → business.country_code
 *   country       → full country name          → business.country (display)
 */
export default function ClientFormModal({ open, initial, onClose, onSaved }) {
  const [f, setF] = useState({
    business_name: "", contact_name: "", email: "", address: "",
    website: "", status: "prospect", notes: "", industry: "",
    territory: "",
    // PhoneCountryFields contract
    phone: "", country: "", country_code: "",
  });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const c = initial.contacts?.[0] || {};
      setF({
        business_name: initial.business?.name || initial.name || "",
        contact_name: c.full_name || "",
        email: c.email || initial.business?.email || initial.email || "",
        phone: c.phone || initial.business?.phone || "",
        address: initial.business?.address || initial.address || "",
        website: (initial.websites?.[0]?.url) || initial.website || "",
        status: initial.business?.status || initial.status || "prospect",
        notes: initial.business?.notes || "",
        industry: initial.business?.industry || "",
        country: initial.business?.country || "",
        country_code: initial.business?.country_code || "",
        territory: initial.business?.territory || initial.territory || "",
      });
    } else {
      setF({ business_name: "", contact_name: "", email: "", phone: "",
             address: "", website: "", status: "prospect", notes: "",
             industry: "", country: "", country_code: "", territory: "" });
    }
  }, [open, initial]);

  if (!open) return null;

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.business_name.trim()) return toast.error("Business name is required");
    if (f.phone && !isPhoneValidOrEmpty(f.phone))
      return toast.error("That phone number doesn't look valid.");
    setSaving(true);
    try {
      // Phase 3I.3F — preserve empty-string clears for optional fields so
      // the operator can blank out a value and have it persist. Only strip
      // truly-untouched required-shape fields on CREATE. Territory is
      // stripped on CREATE (backend defaults it) but sent even when blank
      // on EDIT so it can be explicitly cleared.
      const body = { ...f };
      const CLEARABLE_ON_EDIT = new Set([
        "phone", "email", "address", "website", "notes", "industry",
        "country", "country_code", "territory", "contact_name",
      ]);
      if (isEdit) {
        // Send everything. Empty strings mean "clear this field".
        // business_name may not be blanked (required).
        if (body.business_name === "") delete body.business_name;
      } else {
        // On CREATE, strip empties so backend defaults apply.
        Object.keys(body).forEach((k) => {
          if (body[k] === "" && !CLEARABLE_ON_EDIT.has(k)) delete body[k];
          if (body[k] === "") delete body[k];
        });
      }
      let saved;
      if (isEdit) {
        saved = await hqApi.patch(`/control/clients/${initial.id}`, body);
        toast.success("Client updated");
      } else {
        saved = await hqApi.post("/control/clients", body);
        toast.success("Client created");
      }
      onSaved && onSaved(saved?.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end lg:items-center lg:justify-center p-0 lg:p-6" data-testid="hq-client-form-modal">
      <form onSubmit={submit} className="bg-white w-full lg:w-[95vw] lg:max-w-[820px] lg:rounded-2xl overflow-hidden flex flex-col max-h-full">
        <div className="flex items-start justify-between p-5 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{isEdit ? "Edit client" : "New client"}</div>
            <h2 className="mt-1 font-black text-xl md:text-2xl">{isEdit ? "Update client details" : "Add a client"}</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="hq-client-form-close" className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>

        <div className="p-5 overflow-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Business name *</div>
            <input required data-testid="hq-client-form-name" value={f.business_name} onChange={set("business_name")} placeholder="Acme Ltd" className="w-full px-3 py-2 rounded-lg border"/>
          </label>
          <label className="block">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Contact name</div>
            <input data-testid="hq-client-form-contact" value={f.contact_name} onChange={set("contact_name")} placeholder="Jane Doe" className="w-full px-3 py-2 rounded-lg border"/>
          </label>
          <label className="block">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Email</div>
            <input type="email" data-testid="hq-client-form-email" value={f.email} onChange={set("email")} placeholder="hello@acme.com" className="w-full px-3 py-2 rounded-lg border"/>
          </label>
          <label className="block">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Website</div>
            <input data-testid="hq-client-form-website" value={f.website} onChange={set("website")} placeholder="https://acme.com" className="w-full px-3 py-2 rounded-lg border"/>
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Address</div>
            <input data-testid="hq-client-form-address" value={f.address} onChange={set("address")} placeholder="Street, City, Postcode" className="w-full px-3 py-2 rounded-lg border"/>
          </label>

          {/* Shared PhoneCountryFields — country dropdown + phone country code selector. */}
          <div className="md:col-span-2" data-testid="hq-client-form-phonecountry">
            <PhoneCountryFields
              value={{ phone: f.phone, country: f.country, country_code: f.country_code }}
              onChange={(next) => setF({ ...f, ...next })}
            />
          </div>

          <label className="block">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Industry</div>
            <select
              data-testid="hq-client-form-industry"
              value={f.industry}
              onChange={set("industry")}
              className="w-full px-3 py-2 rounded-lg border"
            >
              <option value="">— Not set —</option>
              {/* Preserve any pre-existing legacy free-text value so it
                  remains visible until the operator picks a canonical slug. */}
              {f.industry && !INDUSTRY_CATALOGUE.some((r) => r.slug === f.industry) && (
                <option value={f.industry}>{industryLabel(f.industry)} (legacy)</option>
              )}
              {INDUSTRY_CATALOGUE.map((row) => (
                <option key={row.slug} value={row.slug}>{row.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Territory</div>
            <select
              data-testid="hq-client-form-territory"
              value={f.territory}
              onChange={set("territory")}
              className="w-full px-3 py-2 rounded-lg border"
            >
              {TERRITORY_OPTIONS.map((t) => <option key={t.value || "not_set"} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Status</div>
            <select data-testid="hq-client-form-status" value={f.status} onChange={set("status")} className="w-full px-3 py-2 rounded-lg border">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Notes</div>
            <textarea data-testid="hq-client-form-notes" value={f.notes} onChange={set("notes")} rows={4} placeholder="Internal notes for the team" className="w-full px-3 py-2 rounded-lg border"/>
          </label>
        </div>

        <div className="p-4 border-t border-[#EAEAEA] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase">Cancel</button>
          <button type="submit" disabled={saving} data-testid="hq-client-form-save" className="px-5 py-2 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50">
            <Save size={12} className="inline mr-1"/> {saving ? "Saving…" : (isEdit ? "Save changes" : "Create client")}
          </button>
        </div>
      </form>
    </div>
  );
}
